import type { NodeTypeDef, ParamBag, ParamDef, ParamValue } from './types';

const types = new Map<string, NodeTypeDef>();

export function registerNodeType(def: NodeTypeDef): void {
  if (types.has(def.id)) throw new Error(`Duplicate node type id: ${def.id}`);
  assertValidDef(def);
  types.set(def.id, def);
}

export function registerNodeTypes(defs: NodeTypeDef[]): void {
  for (const d of defs) registerNodeType(d);
}

export function getNodeType(id: string): NodeTypeDef | undefined {
  return types.get(id);
}

/** Throws if the type is unknown. Use at call sites that cannot proceed without it. */
export function requireNodeType(id: string): NodeTypeDef {
  const t = types.get(id);
  if (!t) throw new Error(`Unknown node type: ${id}`);
  return t;
}

export function allNodeTypes(): NodeTypeDef[] {
  return [...types.values()].sort(
    (a, b) => a.category.localeCompare(b.category) || a.label.localeCompare(b.label),
  );
}

export function clearRegistry(): void {
  types.clear();
}

/** Fresh parameter bag with every default materialised. */
export function defaultParams(def: NodeTypeDef): ParamBag {
  const out: ParamBag = {};
  for (const p of def.params) out[p.name] = cloneValue(p.default);
  return out;
}

export function cloneValue(v: ParamValue): ParamValue {
  if (Array.isArray(v)) return v.map((e) => (typeof e === 'object' ? { ...e } : e)) as ParamValue;
  return v;
}

/**
 * Coerce an arbitrary incoming value into the declared parameter type.
 *
 * Agents send JSON that is close-but-not-exact surprisingly often — `"0.5"`
 * for a float, `1` for a bool, a 3-tuple for an RGBA colour. Rather than
 * rejecting the whole operation we coerce what is unambiguous and throw only
 * when the intent is genuinely unclear.
 */
export function coerceParam(def: ParamDef, raw: unknown): ParamValue {
  switch (def.type) {
    case 'float': {
      const n = toNumber(raw, def.name);
      return clamp(n, def.min, def.max);
    }
    case 'int': {
      const n = Math.round(toNumber(raw, def.name));
      return clamp(n, def.min, def.max);
    }
    case 'bool': {
      if (typeof raw === 'boolean') return raw;
      if (raw === 'true' || raw === 1) return true;
      if (raw === 'false' || raw === 0) return false;
      throw new Error(`Parameter "${def.name}" expects a boolean, received ${JSON.stringify(raw)}`);
    }
    case 'enum': {
      const s = String(raw);
      if (!def.options.some((o) => o.value === s)) {
        const valid = def.options.map((o) => o.value).join(', ');
        throw new Error(`Parameter "${def.name}" expects one of [${valid}], received "${s}"`);
      }
      return s;
    }
    case 'vec2':
      return toVec(raw, 2, def.name).map((n) => clamp(n, def.min, def.max)) as [number, number];
    case 'vec3':
      return toVec(raw, 3, def.name).map((n) => clamp(n, def.min, def.max)) as [
        number,
        number,
        number,
      ];
    case 'color': {
      const v = Array.isArray(raw) && raw.length === 3 ? [...raw, 1] : raw;
      return toVec(v, 4, def.name).map((n) => clamp(n, 0, 1)) as [number, number, number, number];
    }
    case 'curve': {
      if (!Array.isArray(raw) || raw.length < 2) {
        throw new Error(`Parameter "${def.name}" expects at least two curve points`);
      }
      return raw
        .map((point, i) => {
          const pt = point as { x?: unknown; y?: unknown };
          return {
            x: clamp(toNumber(pt.x, `${def.name}[${i}].x`), 0, 1),
            y: clamp(toNumber(pt.y, `${def.name}[${i}].y`), 0, 1),
          };
        })
        .sort((a, b) => a.x - b.x);
    }
    case 'gradient': {
      if (!Array.isArray(raw) || raw.length === 0) {
        throw new Error(`Parameter "${def.name}" expects a non-empty array of gradient stops`);
      }
      return raw
        .map((stop, i) => {
          const s = stop as { t?: unknown; color?: unknown };
          const color = Array.isArray(s.color) && s.color.length === 3 ? [...s.color, 1] : s.color;
          return {
            t: clamp(toNumber(s.t, `${def.name}[${i}].t`), 0, 1),
            color: toVec(color, 4, `${def.name}[${i}].color`).map((n) => clamp(n, 0, 1)) as [
              number,
              number,
              number,
              number,
            ],
          };
        })
        .sort((a, b) => a.t - b.t);
    }
  }
}

/**
 * Validate and merge a partial parameter patch onto an existing bag.
 * Unknown keys are reported rather than silently dropped — a typo in an agent
 * op should surface immediately, not manifest as a node that ignores input.
 */
export function applyParamPatch(
  def: NodeTypeDef,
  current: ParamBag,
  patch: Record<string, unknown>,
): ParamBag {
  const next: ParamBag = { ...current };
  const unknown: string[] = [];
  for (const [key, raw] of Object.entries(patch)) {
    const pdef = def.params.find((p) => p.name === key);
    if (!pdef) {
      unknown.push(key);
      continue;
    }
    next[key] = coerceParam(pdef, raw);
  }
  if (unknown.length) {
    const valid = def.params.map((p) => p.name).join(', ');
    throw new Error(
      `Unknown parameter(s) for "${def.id}": ${unknown.join(', ')}. Valid names: ${valid || '(none)'}`,
    );
  }
  return next;
}

/** Fill in defaults for missing keys and drop keys the type no longer declares. */
export function reconcileParams(def: NodeTypeDef, params: ParamBag): ParamBag {
  const out: ParamBag = {};
  for (const p of def.params) {
    out[p.name] = p.name in params ? safeCoerce(p, params[p.name]) : cloneValue(p.default);
  }
  return out;
}

function safeCoerce(def: ParamDef, raw: unknown): ParamValue {
  try {
    return coerceParam(def, raw);
  } catch {
    return cloneValue(def.default);
  }
}

function toNumber(raw: unknown, name: string): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) {
    throw new Error(`Parameter "${name}" expects a number, received ${JSON.stringify(raw)}`);
  }
  return n;
}

function toVec(raw: unknown, len: number, name: string): number[] {
  if (!Array.isArray(raw) || raw.length !== len) {
    throw new Error(
      `Parameter "${name}" expects an array of ${len} numbers, received ${JSON.stringify(raw)}`,
    );
  }
  return raw.map((v, i) => toNumber(v, `${name}[${i}]`));
}

function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}

function assertValidDef(def: NodeTypeDef): void {
  const seen = new Set<string>();
  for (const p of def.params) {
    if (seen.has(p.name)) throw new Error(`Node "${def.id}" declares parameter "${p.name}" twice`);
    seen.add(p.name);
  }
  const ports = new Set<string>();
  for (const i of def.inputs) {
    if (ports.has(i.name)) throw new Error(`Node "${def.id}" declares input "${i.name}" twice`);
    ports.add(i.name);
  }
  if (def.inputs.length > 4) {
    // The shader prelude binds uIn0..uIn3; raising this means widening it too.
    throw new Error(`Node "${def.id}" declares ${def.inputs.length} inputs; the limit is 4`);
  }
}
